import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Home, Search, Inbox, CalendarDays, Menu, User, LayoutDashboard, Rss, Users, Film, Bell, Settings, LogOut, DollarSign, ChevronDown, Trophy, Award, GraduationCap, CreditCard } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useRole } from '../../contexts/RoleContext';
import { useMobileAppShell } from '../../hooks/useIsMobile';
import { useState, useMemo } from 'react';
import PNDiamondLogo from '../../../assets/PNdiamond.svg';
import { buildMyTeamsMenuItems, navigateToMyTeam } from '../../lib/myTeamsMenu';
import { shouldShowMobileNav } from '../../lib/mobileNavVisibility';


interface NavItem {
  path: string;
  label: string;
  icon: React.ReactNode;
  activeIcon?: React.ReactNode;
  isMenu?: boolean;
}

export function MobileNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, profile, signOut, isCoach } = useAuth();
  const { isViewingAsOrganizer, availableTeams, availableAthleteTeams, setActiveContext } = useRole();
  const mobileAppShell = useMobileAppShell();
  const [menuOpen, setMenuOpen] = useState(false);
  const [eventsOpen, setEventsOpen] = useState(false);
  const [myTeamsOpen, setMyTeamsOpen] = useState(false);

  const myTeamsMenuItems = useMemo(
    () => buildMyTeamsMenuItems(availableTeams, availableAthleteTeams),
    [availableTeams, availableAthleteTeams],
  );

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
    setMenuOpen(false);
  };

  const handleMyTeamClick = (team: (typeof myTeamsMenuItems)[number]) => {
    navigateToMyTeam(team, setActiveContext, navigate);
    setMenuOpen(false);
    setMyTeamsOpen(false);
  };

  if (!shouldShowMobileNav(location.pathname, mobileAppShell)) {
    return null;
  }

  // Active icon style: seafoam green stroke, transparent fill
  const activeIconClass = "w-6 h-6 stroke-[#20B2AA] fill-none";
  const inactiveIconClass = "w-6 h-6";

  // Search button (standalone circle)
  const searchItem: NavItem = {
    path: '/search',
    label: 'Search',
    icon: <Search className={inactiveIconClass} strokeWidth={1.8} />,
    activeIcon: <Search className={activeIconClass} strokeWidth={2.5} />,
  };

  // Middle nav items (will be in pill container)
  const middleNavItems: NavItem[] = [
    {
      path: '/feed',
      label: 'Home',
      icon: <Home className={inactiveIconClass} strokeWidth={1.8} />,
      activeIcon: <Home className={activeIconClass} strokeWidth={2.5} />,
    },
    {
      path: user ? '/communications' : '/login',
      label: 'Inbox',
      icon: <Inbox className={inactiveIconClass} strokeWidth={1.8} />,
      activeIcon: <Inbox className={activeIconClass} strokeWidth={2.5} />,
    },
    {
      path: '/discover',
      label: 'Events',
      icon: <CalendarDays className={inactiveIconClass} strokeWidth={1.8} />,
      activeIcon: <CalendarDays className={activeIconClass} strokeWidth={2.5} />,
    },
    {
      path: '#menu',
      label: 'Menu',
      icon: <Menu className={inactiveIconClass} strokeWidth={1.8} />,
      activeIcon: <Menu className="w-6 h-6 stroke-[#20B2AA] fill-none" strokeWidth={2.5} />,
      isMenu: true,
    },
  ];

  // Profile button (standalone circle)
  const profileItem: NavItem = {
    path: user ? '/dashboard' : '/login',
    label: 'Profile',
    icon: <User className={inactiveIconClass} strokeWidth={1.8} />,
    activeIcon: <User className={activeIconClass} strokeWidth={2.5} />,
  };

  const isActiveRoute = (path: string) => {
    if (path === '#menu') return menuOpen;
    if (path === '/discover') {
      // Match discover and all event pages (tournaments, leagues, clinics)
      return location.pathname.startsWith('/discover') ||
             location.pathname.startsWith('/tournaments') || 
             location.pathname.startsWith('/leagues') ||
             location.pathname.startsWith('/clinics') ||
             location.pathname.startsWith('/events') ||
             location.pathname.startsWith('/live');
    }
    if (path === '/communications') {
      return (
        location.pathname === '/communications' ||
        location.pathname.startsWith('/team-chat/')
      );
    }
    if (path === '/feed') {
      return location.pathname === '/feed';
    }
    if (path === '/search') {
      // Match search and athlete pages
      return location.pathname.startsWith('/search') ||
             location.pathname.startsWith('/athletes');
    }
    if (path === '/dashboard') {
      return location.pathname === '/dashboard' ||
             location.pathname.startsWith('/athlete-dashboard') ||
             location.pathname.startsWith('/my-team') ||
             location.pathname.startsWith('/notification-settings');
    }
    return location.pathname.startsWith(path);
  };

  const handleNavClick = (item: NavItem, e: React.MouseEvent) => {
    if (item.isMenu) {
      e.preventDefault();
      setMenuOpen(!menuOpen);
    } else {
      setMenuOpen(false);
    }
  };

  return (
    <>
      {/* Menu overlay when open */}
      {menuOpen && (
        <div 
          className="fixed inset-0 bg-black/20 z-40 backdrop-blur-sm"
          onClick={() => setMenuOpen(false)}
        />
      )}
      
      {/* Quick menu panel - matches web logged-in dropdown */}
      {menuOpen && (
        <div className="mobile-nav-menu-panel fixed left-4 right-4 z-50 animate-slide-up">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-gray-200 dark:border-slate-700 overflow-hidden max-h-[70vh] overflow-y-auto">
            {/* User Info Header */}
            {user && (
              <div className="px-4 py-3 border-b border-gray-100 dark:border-slate-700 flex items-center gap-3 bg-gray-50 dark:bg-slate-900">
                {profile?.avatar_url ? (
                  <img
                    src={profile.avatar_url}
                    alt={profile.full_name || 'User'}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-10 h-10 bg-teal-100 dark:bg-teal-900/30 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-teal-600 dark:text-teal-400" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 dark:text-slate-100 truncate">{profile?.full_name}</p>
                  <p className="text-xs text-gray-500 dark:text-slate-400 truncate">{user.email}</p>
                </div>
              </div>
            )}
            
            {/* Menu Items */}
            <div className="py-2">
              <Link
                to="/dashboard"
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700 active:bg-gray-100 dark:active:bg-slate-600"
              >
                <LayoutDashboard className="w-5 h-5 text-gray-400 dark:text-slate-400" />
                <span className="text-sm font-medium">My Dashboard</span>
              </Link>
              <Link
                to="/communications"
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700 active:bg-gray-100 dark:active:bg-slate-600"
              >
                <Inbox className="w-5 h-5 text-gray-400 dark:text-slate-400" />
                <span className="text-sm font-medium">My Inbox</span>
              </Link>
              
              {/* Events Dropdown */}
              <button
                onClick={() => setEventsOpen(!eventsOpen)}
                className="flex items-center justify-between w-full px-4 py-3 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700 active:bg-gray-100 dark:active:bg-slate-600"
              >
                <div className="flex items-center gap-3">
                  <CalendarDays className="w-5 h-5 text-gray-400 dark:text-slate-400" />
                  <span className="text-sm font-medium">Events</span>
                </div>
                <ChevronDown className={`w-4 h-4 text-gray-400 dark:text-slate-400 transition-transform ${eventsOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {eventsOpen && (
                <div className="bg-gray-50 dark:bg-slate-900/50">
                  <Link
                    to="/tournaments"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-3 pl-12 pr-4 py-2.5 text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700 active:bg-gray-200 dark:active:bg-slate-600"
                  >
                    <Trophy className="w-4 h-4 text-gray-400 dark:text-slate-400" />
                    <span className="text-sm">Tournaments</span>
                  </Link>
                  <Link
                    to="/leagues"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-3 pl-12 pr-4 py-2.5 text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700 active:bg-gray-200 dark:active:bg-slate-600"
                  >
                    <Award className="w-4 h-4 text-gray-400 dark:text-slate-400" />
                    <span className="text-sm">Leagues</span>
                  </Link>
                  <Link
                    to="/clinics"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-3 pl-12 pr-4 py-2.5 text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700 active:bg-gray-200 dark:active:bg-slate-600"
                  >
                    <GraduationCap className="w-4 h-4 text-gray-400 dark:text-slate-400" />
                    <span className="text-sm">Camps</span>
                  </Link>
                </div>
              )}
              
              <Link
                to="/feed"
                onClick={() => setMenuOpen(false)}
                className="block px-4 py-3 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700 active:bg-gray-100 dark:active:bg-slate-600"
              >
                <span className="text-sm font-medium">Activity Feed</span>
              </Link>
              <Link
                to="/search"
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700 active:bg-gray-100 dark:active:bg-slate-600"
              >
                <Search className="w-5 h-5 text-gray-400 dark:text-slate-400" />
                <span className="text-sm font-medium">Search</span>
              </Link>
              <Link
                to="/teams"
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700 active:bg-gray-100 dark:active:bg-slate-600"
              >
                <Users className="w-5 h-5 text-gray-400 dark:text-slate-400" />
                <span className="text-sm font-medium">Browse Teams</span>
              </Link>
              <Link
                to="/athletes"
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700 active:bg-gray-100 dark:active:bg-slate-600"
              >
                <User className="w-5 h-5 text-gray-400 dark:text-slate-400" />
                <span className="text-sm font-medium">Browse Athletes</span>
              </Link>
              
              {myTeamsMenuItems.length > 0 && (
                <>
                  <button
                    onClick={() => setMyTeamsOpen(!myTeamsOpen)}
                    className="flex items-center justify-between w-full px-4 py-3 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700 active:bg-gray-100 dark:active:bg-slate-600"
                  >
                    <div className="flex items-center gap-3">
                      <Users className="w-5 h-5 text-gray-400 dark:text-slate-400" />
                      <span className="text-sm font-medium">My Teams</span>
                    </div>
                    <ChevronDown className={`w-4 h-4 text-gray-400 dark:text-slate-400 transition-transform ${myTeamsOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {myTeamsOpen && (
                    <div className="bg-gray-50 dark:bg-slate-900/50">
                      {myTeamsMenuItems.map((team) => (
                        <button
                          key={team.id}
                          type="button"
                          onClick={() => handleMyTeamClick(team)}
                          className="flex items-center gap-3 pl-12 pr-4 py-2.5 text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700 active:bg-gray-200 dark:active:bg-slate-600 w-full text-left"
                        >
                          {team.logoUrl ? (
                            <img src={team.logoUrl} alt={team.name} className="w-5 h-5 rounded-full object-cover" />
                          ) : (
                            <div className="w-5 h-5 bg-teal-100 dark:bg-teal-900/30 rounded-full flex items-center justify-center">
                              <Users className="w-3 h-3 text-teal-600 dark:text-teal-400" />
                            </div>
                          )}
                          <span className="text-sm truncate">{team.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
              {isCoach && (
                <Link
                  to="/coach-fees"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700 active:bg-gray-100 dark:active:bg-slate-600"
                >
                  <DollarSign className="w-5 h-5 text-gray-400 dark:text-slate-400" />
                  <span className="text-sm font-medium">Fee Collection</span>
                </Link>
              )}
              
              <Link
                to="/video-vault"
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700 active:bg-gray-100 dark:active:bg-slate-600"
              >
                <Film className="w-5 h-5 text-gray-400 dark:text-slate-400" />
                <span className="text-sm font-medium">Video Vault</span>
              </Link>
              <Link
                to="/notification-settings"
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700 active:bg-gray-100 dark:active:bg-slate-600"
              >
                <Bell className="w-5 h-5 text-gray-400 dark:text-slate-400" />
                <span className="text-sm font-medium">Notification Settings</span>
              </Link>
              <Link
                to="/billing"
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700 active:bg-gray-100 dark:active:bg-slate-600"
              >
                <CreditCard className="w-5 h-5 text-gray-400 dark:text-slate-400" />
                <span className="text-sm font-medium">Billing</span>
              </Link>
              <Link
                to="/settings"
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700 active:bg-gray-100 dark:active:bg-slate-600"
              >
                <Settings className="w-5 h-5 text-gray-400 dark:text-slate-400" />
                <span className="text-sm font-medium">Account Settings</span>
              </Link>
              
              {(isViewingAsOrganizer || profile?.role === 'admin' || profile?.role === 'organizer') && (
                <>
                  <hr className="my-2 border-gray-100 dark:border-slate-700" />
                  <Link
                    to="/admin"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-teal-600 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/30 active:bg-teal-100 dark:active:bg-teal-900/50"
                  >
                    <Settings className="w-5 h-5" />
                    <span className="text-sm font-medium">Admin Panel</span>
                  </Link>
                </>
              )}
              
              {user && (
                <>
                  <hr className="my-2 border-gray-100 dark:border-slate-700" />
                  <button
                    onClick={handleSignOut}
                    className="flex items-center gap-3 px-4 py-3 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 active:bg-red-100 dark:active:bg-red-900/50 w-full text-left"
                  >
                    <LogOut className="w-5 h-5" />
                    <span className="text-sm font-medium">Sign Out</span>
                  </button>
                </>
              )}
            </div>
            
            {/* Footer */}
            <div className="py-3 border-t border-gray-100 dark:border-slate-700 flex flex-col items-center gap-2">
              <span className="text-xs text-gray-500 dark:text-slate-400 tracking-wide">Powered by Podium Nation</span>
              <img src={PNDiamondLogo} alt="Podium Nation" className="w-8 h-8" />
            </div>
          </div>
        </div>
      )}
      
      <nav className="neumorphic-nav-bar">
        <div className="flex items-center justify-between px-2 gap-3">
          {/* Search - Standalone Circle */}
          <Link
            to={searchItem.path}
            onClick={(e) => handleNavClick(searchItem, e)}
            className={`neumorphic-nav-item-circle ${isActiveRoute(searchItem.path) ? 'active' : ''}`}
            aria-label={searchItem.label}
          >
            <div className={`neumorphic-nav-icon ${!isActiveRoute(searchItem.path) ? 'text-gray-500' : ''}`}>
              {isActiveRoute(searchItem.path) ? searchItem.activeIcon : searchItem.icon}
            </div>
          </Link>

          {/* Middle Items - Pill Container */}
          <div className="neumorphic-nav-pill">
            {middleNavItems.map((item) => {
              const isActive = isActiveRoute(item.path);
              const className = `neumorphic-nav-pill-item ${isActive ? 'active' : ''}`;
              const iconElement = (
                <div className={`neumorphic-nav-icon ${!isActive ? 'text-gray-500' : ''}`}>
                  {isActive ? item.activeIcon : item.icon}
                </div>
              );
              
              if (item.isMenu) {
                return (
                  <button
                    key={item.path + item.label}
                    onClick={(e) => handleNavClick(item, e)}
                    className={className}
                    aria-label={item.label}
                  >
                    {iconElement}
                  </button>
                );
              }
              
              return (
                <Link
                  key={item.path + item.label}
                  to={item.path}
                  onClick={(e) => handleNavClick(item, e)}
                  className={className}
                  aria-label={item.label}
                >
                  {iconElement}
                </Link>
              );
            })}
          </div>

          {/* Profile - Standalone Circle */}
          <Link
            to={profileItem.path}
            onClick={(e) => handleNavClick(profileItem, e)}
            className={`neumorphic-nav-item-circle ${isActiveRoute(profileItem.path) ? 'active' : ''}`}
            aria-label={profileItem.label}
          >
            <div className={`neumorphic-nav-icon ${!isActiveRoute(profileItem.path) ? 'text-gray-500' : ''}`}>
              {isActiveRoute(profileItem.path) ? profileItem.activeIcon : profileItem.icon}
            </div>
          </Link>
        </div>
      </nav>
    </>
  );
}

